import React, { useState } from 'react';
import { Link as RouterLink, Navigate, Route, Routes, useSearchParams } from 'react-router-dom';
import {
  Alert,
  AppBar,
  Box,
  Button,
  Container,
  FormControlLabel,
  LinearProgress,
  Switch,
  Toolbar,
  Typography,
} from '@mui/material';
import AddArtworkForm from './components/AddArtworkForm';
import ArtworkTable from './components/ArtworkTable';
import ArtworkSearch from './components/ArtworkSearch';
import ArtworkPagination from './components/ArtworkPagination';
import EditArtworkModal from './components/EditArtworkModal';
import AuthGuard from './components/AuthGuard';
import LumeneLogo from './components/LumeneLogo';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Login from './pages/Login';
import Users from './pages/Users';
import { useArtworkList } from './hooks/useArtworkList';
import { Artwork } from './types/artwork';
import { apiDelete } from './api/client';

type Props = {
  toggleMode?: () => void;
  mode?: 'light' | 'dark';
};

// ── Catalog page (the existing content, now a standalone component) ──────────

const CatalogPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedArtwork, setSelectedArtwork] = useState<Artwork | null>(null);

  const { items, total, page, limit, q, loading, error, refresh } = useArtworkList();

  const handleUploadSuccess = () => {
    setSearchParams({ page: '1', q: '' });
  };

  const handlePostDelete = () => {
    const newTotal = total - 1;
    const maxPage = Math.max(1, Math.ceil(newTotal / limit));
    if (page > maxPage) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('page', String(maxPage));
        return next;
      });
    } else {
      refresh();
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const res = await apiDelete(`/api/admin/artworks/${id}`);
      if (res.ok) {
        handlePostDelete();
      } else {
        alert('Erreur lors de la suppression');
      }
    } catch {
      alert('Erreur réseau');
    }
  };

  const showEmptyCatalog = !loading && !error && total === 0 && q.trim() === '';
  const showNoResults = !loading && !error && total === 0 && q.trim() !== '';

  return (
    <Container>
      <Typography variant="h4" component="h1" sx={{ mt: 4, mb: 2 }}>
        Museum Artwork Admin
      </Typography>

      <ArtworkSearch />

      <LinearProgress sx={{ my: 1, visibility: loading ? 'visible' : 'hidden' }} />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {showEmptyCatalog && (
        <Box textAlign="center" mt={4}>
          <Typography>No artworks yet.</Typography>
          <Button
            variant="outlined"
            sx={{ mt: 1 }}
            onClick={() => {
              document.querySelector<HTMLInputElement>('input[name="title"]')?.focus();
            }}
          >
            Upload your first artwork
          </Button>
        </Box>
      )}

      {showNoResults && (
        <Box textAlign="center" mt={4}>
          <Typography>No artworks match &ldquo;{q}&rdquo;.</Typography>
          <Button
            variant="text"
            sx={{ mt: 1 }}
            onClick={() => setSearchParams({ page: '1' })}
          >
            Clear search
          </Button>
        </Box>
      )}

      {!showEmptyCatalog && !showNoResults && (
        <ArtworkTable artworks={items} onDelete={handleDelete} onEdit={setSelectedArtwork} />
      )}

      <ArtworkPagination total={total} page={page} limit={limit} loading={loading} />

      <AddArtworkForm onSuccess={handleUploadSuccess} />

      {selectedArtwork && (
        <EditArtworkModal
          artwork={selectedArtwork}
          onClose={() => setSelectedArtwork(null)}
          onUpdated={() => setSearchParams((prev) => new URLSearchParams(prev))}
          onDeleted={() => {
            setSelectedArtwork(null);
            handlePostDelete();
          }}
        />
      )}
    </Container>
  );
};

// ── Navigation header ─────────────────────────────────────────────────────────

interface NavHeaderProps {
  toggleMode?: () => void;
  mode?: 'light' | 'dark';
}

const NavHeader: React.FC<NavHeaderProps> = ({ toggleMode, mode }) => {
  const { user, logout } = useAuth();

  return (
    <AppBar position="static" color="default" elevation={1}>
      <Toolbar sx={{ gap: 2 }}>
        <LumeneLogo size="sm" variant={mode === 'dark' ? 'dark' : 'light'} />

        {user && (
          <>
            <Button component={RouterLink} to="/" color="inherit">
              Catalog
            </Button>
            {user.role === 'admin' && (
              <Button component={RouterLink} to="/users" color="inherit">
                Users
              </Button>
            )}
          </>
        )}

        <Box sx={{ flexGrow: 1 }} />

        <FormControlLabel
          control={<Switch checked={mode === 'dark'} onChange={toggleMode} />}
          label={mode === 'dark' ? '🌙 Dark Mode' : '☀️ Light Mode'}
        />

        {user && (
          <Button color="inherit" onClick={logout}>
            Logout
          </Button>
        )}
      </Toolbar>
    </AppBar>
  );
};

// ── Root app component ────────────────────────────────────────────────────────

const App: React.FC<Props> = ({ toggleMode, mode }) => {
  return (
    <AuthProvider>
      <NavHeader toggleMode={toggleMode} mode={mode} />
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route
          path="/users"
          element={
            <AuthGuard>
              <Users />
            </AuthGuard>
          }
        />
        <Route
          path="/"
          element={
            <AuthGuard>
              <CatalogPage />
            </AuthGuard>
          }
        />
        <Route
          path="*"
          element={
            <AuthGuard>
              <Navigate to="/" replace />
            </AuthGuard>
          }
        />
      </Routes>
    </AuthProvider>
  );
};

export default App;
